import { PageHeader } from '@/components/PageHeader/PageHeader.tsx'
import { Box, Button, Container, Typography } from '@mui/material'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserForm } from '../-components/UserForm.tsx'
import { useEditUser } from '@/features/user/hooks'
import { useTenant } from '@/contexts'
import {
  useGetUser,
  userQueryOptions
} from '@/features/user/hooks/useEmployeeUserList.ts'
import { queryClient } from '@/utils/query-client.ts'

export const Route = createFileRoute('/_authenticated/$companySlug/users/$userId/edit')({
  loader: ({ params }) => queryClient.ensureQueryData(userQueryOptions(params.userId)),
  beforeLoad: async ({ context }) => {
    context.auth.rejectIfMissingPermissions(['USER_READ', 'USER_WRITE'])

    if (context.company.access !== 'COMPANY_WIDE') {
      throw redirect({
        to: '/forbidden',
        search: { redirect: location.href }
      })
    }
  },
  component: EditUser
})

function EditUser() {
  const { t } = useTranslation(['_routes', '_global', '@feature_user'])
  const { userId } = Route.useParams()
  const navigate = Route.useNavigate()
  const { tenantId } = useTenant()
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [error, setError] = useState<Error | null>(null)

  const { data: userData, isLoading } = useGetUser(userId)

  const editUserMutation = useEditUser({
    userId,
    tenantId,
    onSuccess: async () => {
      setSuccessMessage(t('routes.users_edit.main.messages.successMessage'))
      setError(null)
      // Navigate back to users list after successful edit
      setTimeout(() => {
        navigate({ to: '/$companySlug/users' })
      }, 2000)
    },
    onError: err => {
      setError(err)
      setSuccessMessage(null)
    }
  })

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 2, marginLeft: 0 }}>
        <Typography>Loading...</Typography>
      </Container>
    )
  }

  if (!userData) {
    return (
      <Container maxWidth="xl" sx={{ py: 2, marginLeft: 0 }}>
        <Typography>User not found</Typography>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2, marginLeft: 0 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <PageHeader
          backArrow={{ to: '/$companySlug/users', from: Route.fullPath }}
          subtitle={t('routes.users_edit.main.description')}
        >
          {t('routes.users_edit.main.title')}
        </PageHeader>
      </Box>

      <UserForm
        existingUser={userData}
        onSubmit={editUserMutation.mutate}
        successMessage={successMessage}
        error={error}
        onClearSuccess={() => setSuccessMessage(null)}
        onClearError={() => setError(null)}
        slots={{
          submit: props => (
            <Button {...props}>{t('routes.users_edit.main.submit.available')}</Button>
          )
        }}
      />
    </Container>
  )
}
